import { useState, useEffect } from 'react';
import { usersAPI, departmentsAPI } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { formatDate, formatRelativeTime } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Users, Loader2, UserPlus, Mail, Lock, User, RefreshCw, Copy, Check, Edit, Trash2, Home } from 'lucide-react';
import axios from 'axios';

import { BACKEND_URL } from '@/lib/backend';
const API_BASE = BACKEND_URL + '/api';

export default function UserManagement() {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [copied, setCopied] = useState(false);
  const [useRandomPassword, setUseRandomPassword] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    password: '',
    role: 'agent',
    department_id: '',
    tier: 1
  });

  const [editData, setEditData] = useState({
    name: '',
    email: '',
    password: '',
    role: '',
    department_id: '',
    tier: 1
  });

  useEffect(() => {
    loadData();
  }, [roleFilter]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [usersRes, deptsRes] = await Promise.all([
        usersAPI.list(),
        departmentsAPI.list()
      ]);
      
      // Filter out customers - only show admins and agents
      const filteredUsers = usersRes.data.filter(u => u.role !== 'customer');
      
      // Apply role filter
      const finalUsers = roleFilter === 'all' 
        ? filteredUsers 
        : filteredUsers.filter(u => u.role === roleFilter);
      
      setUsers(finalUsers);
      setDepartments(deptsRes.data);
    } catch (err) {
      console.error('Failed to load users:', err);
    } finally {
      setLoading(false);
    }
  };

  const generateRandomPassword = async (forEdit = false) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_BASE}/auth/generate-password`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (forEdit) {
        setEditData(prev => ({ ...prev, password: res.data.password }));
      } else {
        setNewUser(prev => ({ ...prev, password: res.data.password }));
      }
    } catch (err) {
      const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$%';
      let password = '';
      for (let i = 0; i < 12; i++) {
        password += chars.charAt(Math.floor(Math.random() * chars.length));
      }
      if (forEdit) {
        setEditData(prev => ({ ...prev, password }));
      } else {
        setNewUser(prev => ({ ...prev, password }));
      }
    }
  };

  const openCreateDialog = () => {
    setNewUser({
      name: '',
      email: '',
      password: '',
      role: 'agent',
      department_id: '',
      tier: 1
    });
    setError('');
    setSuccess('');
    setUseRandomPassword(false);
    setShowCreateDialog(true);
  };

  const openEditDialog = (user) => {
    setEditingUser(user);
    setEditData({
      name: user.name,
      email: user.email,
      password: '',
      role: user.role,
      department_id: user.department_id || '',
      tier: user.tier || 1,
    });
    setError('');
    setSuccess('');
    setUseRandomPassword(false);
    setShowEditDialog(true);
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setCreating(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      const userData = {
        name: newUser.name,
        email: newUser.email,
        password: newUser.password,
        role: newUser.role,
        department_id: newUser.department_id || undefined,
        tier: ['agent', 'admin'].includes(newUser.role) ? (newUser.tier || 1) : undefined,
      };

      await axios.post(`${API_BASE}/auth/register`, userData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSuccess(`User "${newUser.name}" created successfully!`);
      loadData();
      
      setTimeout(() => {
        setShowCreateDialog(false);
        setSuccess('');
      }, 2000);
    } catch (err) {
      console.error('User creation error:', err);
      let errorMessage = 'Failed to create user';
      
      if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      } else if (err.response?.status === 400) {
        errorMessage = 'Invalid user data. Please check all fields.';
      } else if (err.response?.status === 401) {
        errorMessage = 'Not authenticated. Please login again as admin.';
      } else if (err.response?.status === 403) {
        errorMessage = 'Permission denied. Only admins can create users.';
      } else if (err.message) {
        errorMessage = `Error: ${err.message}`;
      }
      
      setError(errorMessage);
    } finally {
      setCreating(false);
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    setEditing(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      const updatePayload = {
        name: editData.name,
        email: editData.email,
        role: editData.role,
        department_id: editData.department_id || undefined,
        tier: ['agent', 'admin'].includes(editData.role) ? (editData.tier || 1) : undefined,
      };

      // Only include password if it's been changed
      if (editData.password) {
        updatePayload.password = editData.password;
      }

      await axios.patch(`${API_BASE}/users/${editingUser.id}`, updatePayload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSuccess(`User "${editData.name}" updated successfully!`);
      loadData();
      
      setTimeout(() => {
        setShowEditDialog(false);
        setEditingUser(null);
        setSuccess('');
      }, 2000);
    } catch (err) {
      console.error('User update error:', err);
      let errorMessage = 'Failed to update user';
      
      if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      } else if (err.message) {
        errorMessage = `Error: ${err.message}`;
      }
      
      setError(errorMessage);
    } finally {
      setEditing(false);
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to delete user "${userName}"?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE}/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess(`User deleted successfully`);
      loadData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete user');
      setTimeout(() => setError(''), 3000);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const roleColors = {
    admin: 'bg-purple-100 text-purple-700 border-purple-200',
    agent: 'bg-cyan-100 text-cyan-700 border-cyan-200'
  };

  if (!isAdmin) {
    return (
      <div className="p-8 text-center">
        <Users className="w-12 h-12 mx-auto mb-4 text-slate-300" />
        <h2 className="text-lg font-medium text-slate-700">Access Denied</h2>
        <p className="text-slate-500">Only administrators can manage users.</p>
      </div>
    );
  }

  return (
    <div className="flex-1 p-4 md:p-6 lg:p-8 overflow-y-auto" data-testid="user-management">
      {/* Back to Dashboard Button */}
      <Button 
        variant="ghost" 
        className="mb-4 text-slate-600 hover:text-slate-900"
        onClick={() => navigate('/dashboard')}
      >
        <Home className="w-4 h-4 mr-2" />
        Back to Dashboard
      </Button>

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">User Management</h1>
          <p className="text-slate-500">Manage admin and agent accounts</p>
        </div>
        <Button 
          className="rounded-full bg-cyan-500 hover:bg-cyan-600 text-white"
          onClick={openCreateDialog}
          data-testid="create-user-btn"
        >
          <UserPlus className="w-4 h-4 mr-2" />
          Create User
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-4 mb-6">
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger className="w-48 bg-white border-slate-200">
            <SelectValue placeholder="Filter by role" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Roles</SelectItem>
            <SelectItem value="admin">Admins</SelectItem>
            <SelectItem value="agent">Agents</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {success && !showCreateDialog && !showEditDialog && (
        <div className="mb-4 bg-emerald-50 border border-emerald-200 text-emerald-600 px-4 py-3 rounded-xl text-sm">
          {success}
        </div>
      )}

      {error && !showCreateDialog && !showEditDialog && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl text-sm">
          {error}
        </div>
      )}

      {/* Users Table */}
      <Card className="bg-white border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-cyan-500" />
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p className="text-slate-500">No users found</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>User</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Tier</TableHead>
                  <TableHead>Department</TableHead>
                  <TableHead>Last login</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar className="w-8 h-8 border border-slate-200">
                          <AvatarImage src={user.avatar_url} />
                          <AvatarFallback className="bg-cyan-100 text-cyan-600">
                            {user.name?.charAt(0)}
                          </AvatarFallback>
                        </Avatar>
                        <span className="font-medium text-slate-800">{user.name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-600">{user.email}</TableCell>
                    <TableCell>
                      <Badge className={`capitalize ${roleColors[user.role]}`}>
                        {user.role}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {['agent', 'admin'].includes(user.role) ? (
                        <Badge
                          variant="outline"
                          className={
                            user.tier === 3
                              ? 'border-purple-300 text-purple-700 bg-purple-50'
                              : user.tier === 2
                              ? 'border-amber-300 text-amber-700 bg-amber-50'
                              : 'border-cyan-300 text-cyan-700 bg-cyan-50'
                          }
                          data-testid={`user-tier-badge-${user.id}`}
                        >
                          Tier {user.tier || 1}
                        </Badge>
                      ) : (
                        <span className="text-slate-400 text-xs">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-slate-600 text-sm">
                      {departments.find(d => d.id === user.department_id)?.name || '-'}
                    </TableCell>
                    <TableCell className="text-slate-500 text-sm" data-testid={`user-last-login-${user.id}`}>
                      {user.last_login_at ? (
                        <span title={formatDate(user.last_login_at)}>
                          {formatRelativeTime(user.last_login_at)}
                        </span>
                      ) : (
                        <span className="text-slate-400 italic">never</span>
                      )}
                    </TableCell>
                    <TableCell className="text-slate-500 text-sm">
                      {formatDate(user.created_at).split(',')[0]}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => openEditDialog(user)}
                        >
                          <Edit className="w-4 h-4 text-cyan-600" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleDeleteUser(user.id, user.name)}
                        >
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create User Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="bg-white border-slate-200 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-slate-800">Create New User</DialogTitle>
            <DialogDescription className="text-slate-500">
              Add a new admin or agent to the system
            </DialogDescription>
          </DialogHeader>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl text-sm">
              {error}
            </div>
          )}

          {success && (
            <div className="bg-emerald-50 border border-emerald-200 text-emerald-600 px-4 py-3 rounded-xl text-sm">
              {success}
            </div>
          )}

          <form onSubmit={handleCreateUser} className="space-y-4 mt-4">
            {/* Role Selection */}
            <div className="space-y-2">
              <Label className="text-slate-700">Role *</Label>
              <Select value={newUser.role} onValueChange={(value) => setNewUser(prev => ({ ...prev, role: value }))}>
                <SelectTrigger className="bg-white border-slate-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="agent">Agent</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Tier Selection (agents + admins) */}
            {['agent', 'admin'].includes(newUser.role) && (
              <div className="space-y-2">
                <Label className="text-slate-700">Support Tier *</Label>
                <Select
                  value={String(newUser.tier || 1)}
                  onValueChange={(value) => setNewUser(prev => ({ ...prev, tier: parseInt(value, 10) }))}
                >
                  <SelectTrigger className="bg-white border-slate-200" data-testid="create-user-tier">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Tier 1 — Basic Support</SelectItem>
                    <SelectItem value="2">Tier 2 — Advanced Support</SelectItem>
                    <SelectItem value="3">Tier 3 — Super Agent</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">
                  Tickets are routed to Tier 1 by default. Urgent tickets go to Tier 2. Tier 2/3 handle escalations.
                </p>
              </div>
            )}

            {/* Full Name */}
            <div className="space-y-2">
              <Label className="text-slate-700">Full Name *</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  value={newUser.name}
                  onChange={(e) => setNewUser(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="John Doe"
                  className="pl-10 bg-white border-slate-200"
                  required
                />
              </div>
            </div>

            {/* Email */}
            <div className="space-y-2">
              <Label className="text-slate-700">Email *</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="john@company.com"
                  className="pl-10 bg-white border-slate-200"
                  required
                />
              </div>
            </div>

            {/* Password */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-slate-700">Password *</Label>
                <div className="flex items-center gap-2">
                  <Checkbox 
                    id="random-pw" 
                    checked={useRandomPassword}
                    onCheckedChange={(checked) => {
                      setUseRandomPassword(checked);
                      if (checked) generateRandomPassword(false);
                    }}
                  />
                  <Label htmlFor="random-pw" className="text-xs text-slate-500 cursor-pointer">
                    Generate random
                  </Label>
                </div>
              </div>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    type={useRandomPassword ? "text" : "password"}
                    value={newUser.password}
                    onChange={(e) => setNewUser(prev => ({ ...prev, password: e.target.value }))}
                    placeholder="••••••••"
                    className="pl-10 bg-white border-slate-200"
                    required
                    minLength={6}
                  />
                </div>
                {useRandomPassword && (
                  <>
                    <Button type="button" variant="outline" size="icon" onClick={() => generateRandomPassword(false)}>
                      <RefreshCw className="w-4 h-4" />
                    </Button>
                    <Button type="button" variant="outline" size="icon" onClick={() => copyToClipboard(newUser.password)}>
                      {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </>
                )}
              </div>
              {useRandomPassword && newUser.password && (
                <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2 rounded-lg">
                  <strong>Save this password:</strong> <code className="font-mono bg-white px-1 rounded">{newUser.password}</code>
                </div>
              )}
            </div>

            {/* Department */}
            {departments.length > 0 && (
              <div className="space-y-2">
                <Label className="text-slate-700">Department</Label>
                <Select 
                  value={newUser.department_id} 
                  onValueChange={(value) => setNewUser(prev => ({ ...prev, department_id: value }))}
                >
                  <SelectTrigger className="bg-white border-slate-200">
                    <SelectValue placeholder="Select department (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    {departments.map((dept) => (
                      <SelectItem key={dept.id} value={dept.id}>
                        {dept.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
              <Button type="button" variant="ghost" onClick={() => setShowCreateDialog(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-cyan-500 hover:bg-cyan-600 text-white rounded-full"
                disabled={creating}
              >
                {creating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Create User'
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="bg-white border-slate-200 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-slate-800">Edit User</DialogTitle>
            <DialogDescription className="text-slate-500">
              Update user information and credentials
            </DialogDescription>
          </DialogHeader>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl text-sm">
              {error}
            </div>
          )}

          {success && (
            <div className="bg-emerald-50 border border-emerald-200 text-emerald-600 px-4 py-3 rounded-xl text-sm">
              {success}
            </div>
          )}

          <form onSubmit={handleUpdateUser} className="space-y-4 mt-4">
            {/* Role Selection */}
            <div className="space-y-2">
              <Label className="text-slate-700">Role *</Label>
              <Select value={editData.role} onValueChange={(value) => setEditData(prev => ({ ...prev, role: value }))}>
                <SelectTrigger className="bg-white border-slate-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="agent">Agent</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Tier Selection (agents + admins) */}
            {['agent', 'admin'].includes(editData.role) && (
              <div className="space-y-2">
                <Label className="text-slate-700">Support Tier *</Label>
                <Select
                  value={String(editData.tier || 1)}
                  onValueChange={(value) => setEditData(prev => ({ ...prev, tier: parseInt(value, 10) }))}
                >
                  <SelectTrigger className="bg-white border-slate-200" data-testid="edit-user-tier">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Tier 1 — Basic Support</SelectItem>
                    <SelectItem value="2">Tier 2 — Advanced Support</SelectItem>
                    <SelectItem value="3">Tier 3 — Super Agent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Full Name */}
            <div className="space-y-2">
              <Label className="text-slate-700">Full Name *</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  value={editData.name}
                  onChange={(e) => setEditData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="John Doe"
                  className="pl-10 bg-white border-slate-200"
                  required
                />
              </div>
            </div>

            {/* Email */}
            <div className="space-y-2">
              <Label className="text-slate-700">Email *</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  type="email"
                  value={editData.email}
                  onChange={(e) => setEditData(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="john@company.com"
                  className="pl-10 bg-white border-slate-200"
                  required
                />
              </div>
            </div>

            {/* Password */}
            <div className="space-y-2 border-t pt-4">
              <div className="flex items-center justify-between">
                <Label className="text-slate-700">New Password (optional)</Label>
                <div className="flex items-center gap-2">
                  <Checkbox 
                    id="edit-random-pw" 
                    checked={useRandomPassword}
                    onCheckedChange={(checked) => {
                      setUseRandomPassword(checked);
                      if (checked) generateRandomPassword(true);
                      else setEditData(prev => ({ ...prev, password: '' }));
                    }}
                  />
                  <Label htmlFor="edit-random-pw" className="text-xs text-slate-500 cursor-pointer">
                    Generate random
                  </Label>
                </div>
              </div>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    type={useRandomPassword ? "text" : "password"}
                    value={editData.password}
                    onChange={(e) => setEditData(prev => ({ ...prev, password: e.target.value }))}
                    placeholder="Leave empty to keep current password"
                    className="pl-10 bg-white border-slate-200"
                    minLength={6}
                  />
                </div>
                {useRandomPassword && (
                  <>
                    <Button type="button" variant="outline" size="icon" onClick={() => generateRandomPassword(true)}>
                      <RefreshCw className="w-4 h-4" />
                    </Button>
                    <Button type="button" variant="outline" size="icon" onClick={() => copyToClipboard(editData.password)}>
                      {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </>
                )}
              </div>
              {useRandomPassword && editData.password && (
                <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2 rounded-lg">
                  <strong>Save this password:</strong> <code className="font-mono bg-white px-1 rounded">{editData.password}</code>
                </div>
              )}
              <p className="text-xs text-slate-400">Leave empty to keep the current password</p>
            </div>

            {/* Department */}
            {departments.length > 0 && (
              <div className="space-y-2">
                <Label className="text-slate-700">Department</Label>
                <Select 
                  value={editData.department_id} 
                  onValueChange={(value) => setEditData(prev => ({ ...prev, department_id: value }))}
                >
                  <SelectTrigger className="bg-white border-slate-200">
                    <SelectValue placeholder="Select department (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    {departments.map((dept) => (
                      <SelectItem key={dept.id} value={dept.id}>
                        {dept.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
              <Button type="button" variant="ghost" onClick={() => {
                setShowEditDialog(false);
                setEditingUser(null);
              }}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-cyan-500 hover:bg-cyan-600 text-white rounded-full"
                disabled={editing}
              >
                {editing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Updating...
                  </>
                ) : (
                  'Update User'
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
